import { useCallback, useContext, useEffect } from 'react';
import { AuthContext } from 'context';
// Design System
import AlertComponent from 'components/design-system/Alert/Alert';
import ButtonComponent from 'components/design-system/Button/Button';
import Heading from 'components/design-system/Heading/Heading';
import IconButtonComponent from 'components/design-system/IconButton/IconButton';
import Table from 'components/design-system/Table/Table';
import Text from 'components/design-system/Text/Text';
import { SidebarLayout } from 'layouts/Sidebar/SidebarLayout';
// Hooks
import { useColumnTablesListadoGrupos } from '../hooks/useColumnTablesListadoGrupos';
// Utils
import { diccionarioVista } from '../utils/utils';
// Material
import { Add, Close, Delete, Edit } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Hidden,
  Tooltip,
  DialogActions
} from '@mui/material';
// Hooks
import { useServicesGrupos } from '../hooks/useServicesGrupos';
import { useHelpersGrupos } from '../hooks/useHelpersGrupos';
// Constants
import { PERMISOS } from 'const/Permisos';
const Grupos = () => {
  const { isTienePermisos } = useContext(AuthContext);
  // Columns
  const columnTables = useColumnTablesListadoGrupos();
  // Helpers
  const {
    handleClickIngresar,
    handleClickEditar,
    handleClickEliminar,
    handleClickOpen,
    handleClose,
    setOpen,
    open,
    selectedRow
  } = useHelpersGrupos();

  // Exportaciones de useServicesRecursosAdquiridos
  const {
    //Funciones
    ObtieneListaGrupos,
    // States
    listaGrupos,
    setListaGrupos,
    columnFilters,
    globalFilter,
    isLoadingLista,
    pagination,
    rowCount,
    setColumnFilters,
    setGlobalFilter,
    setIsLoadingLista,
    setPagination,
    setRowCount
  } = useServicesGrupos();

  const cargaGrupos = useCallback(async () => {
    ObtieneListaGrupos(
      columnFilters,
      setIsLoadingLista,
      setListaGrupos,
      pagination,
      setRowCount
    );
  }, [
    ObtieneListaGrupos,
    columnFilters,
    pagination,
    setIsLoadingLista,
    setListaGrupos,
    setRowCount
  ]);

  useEffect(() => {
    cargaGrupos(
      columnFilters,
      setIsLoadingLista,
      setListaGrupos,
      pagination,
      setRowCount
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    columnFilters,
    pagination,
    setIsLoadingLista,
    setListaGrupos,
    setRowCount
  ]);

  return (
    <SidebarLayout>
      <div className="mb-10">
        <div className="flex flex-col sm:flex-row sm:items-center mb-4 sm:justify-between gap-4">
          <div>
            <Heading type="h2" className="mb-0">
              {diccionarioVista.tituloListado || 'Sin título'}
            </Heading>
            <Text>
              {isTienePermisos(PERMISOS.sucursales)
                ? diccionarioVista.subtituloListadoSinAcciones
                : diccionarioVista.subtituloListado}
            </Text>
          </div>
          {isTienePermisos(PERMISOS.crearGrupo) && (
            <ButtonComponent type="primary" onClick={handleClickIngresar}>
              <Add className="mr-2" />{' '}
              {`Nuevo ${diccionarioVista.nombrePrincipal}`}
            </ButtonComponent>
          )}
        </div>
        <Table
          className=""
          headTable={
            <Heading type="h4" className="p-2">
              {diccionarioVista.tituloTabla || 'Sin título'}
            </Heading>
          }
          data={listaGrupos || []}
          columns={columnTables}
          rowCount={rowCount}
          isLoading={isLoadingLista}
          enablePagination={true}
          enableRowActions={
            isTienePermisos(PERMISOS.editarGrupo) ||
            isTienePermisos(PERMISOS.eliminarGrupo)
          }
          enableGlobalFilter={false}
          onPaginationChange={setPagination}
          state={{
            columnFilters,
            pagination,
            isLoading: isLoadingLista,
            showProgressBars: isLoadingLista,
            globalFilter,
            columnVisibility: {
              nombreNegocioOrigen: isTienePermisos(PERMISOS.sucursales)
            }
          }}
          manualPagination
          actions={({ row, table }) => (
            <div>
              {isTienePermisos(PERMISOS.editarGrupo) && (
                <Tooltip arrow placement="left" title="Editar">
                  <IconButtonComponent
                    size="small"
                    className="mr-2"
                    disabled={isLoadingLista}
                    type={isLoadingLista ? 'disabled' : 'secondary'}
                    onClick={() => handleClickEditar(row, table)}
                  >
                    <Edit />
                  </IconButtonComponent>
                </Tooltip>
              )}
              {isTienePermisos(PERMISOS.eliminarGrupo) && (
                <Tooltip arrow placement="right" title="Eliminar">
                  <IconButtonComponent
                    size="small"
                    disabled={isLoadingLista}
                    type={isLoadingLista ? 'disabled' : 'error'}
                    onClick={() => handleClickOpen(row, table)}
                  >
                    <Delete />
                  </IconButtonComponent>
                </Tooltip>
              )}
            </div>
          )}
          onColumnFiltersChange={setColumnFilters}
          onGlobalFilterChange={setGlobalFilter}
        />
      </div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          id="alert-dialog-title"
          className="border-b border-neutral-90"
        >
          <Heading type="h3" className="mb-0">
            Confirmación de eliminación del{' '}
            {diccionarioVista.primerNombrePrincipal}{' '}
          </Heading>
        </DialogTitle>
        <DialogContent className="p-5">
          <DialogContentText
            id="alert-dialog-description"
            className="pt-4 pb-0"
          >
            <Text className="pb-4">
              ¿Estás seguro que deseas eliminar completamente este{' '}
              {diccionarioVista.primerNombrePrincipal}?
            </Text>

            <AlertComponent type="error">
              Esta operación es irreversible y{' '}
              <b>
                se perderán todos los datos asociados a este{' '}
                {diccionarioVista.primerNombrePrincipal}
              </b>
            </AlertComponent>
          </DialogContentText>
        </DialogContent>
        <DialogActions className="border-t border-neutral-90 p-4 justify-between">
          <ButtonComponent
            type="secondary"
            onClick={handleClose}
            className="justify-center"
          >
            <Hidden smDown>Cancelar</Hidden>
            <Hidden smUp>
              <Close />
            </Hidden>
          </ButtonComponent>
          <ButtonComponent
            type="primary"
            onClick={() =>
              handleClickEliminar(
                selectedRow,
                listaGrupos,
                setListaGrupos,
                setOpen,
                cargaGrupos
              )
            }
            autoFocus
            className="w-full xs:w-auto justify-center"
          >
            Confirmar
          </ButtonComponent>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
};

export default Grupos;
